/** 
 * Agência Mofo Design
 * Código personalizado produzido pela Mofo para criação isolada de web components e comportamentos únicos não nativos do tema
 * Usualmente, realizamos a alteração de algumas funcionalidades internamente para evitarmos duplicação de componentes e semelhantes,
 * deixando novos elementos em .js isolados.
 * Porém, como explicado na documentação técnica deste tema (https://support.maestrooo.com/category/749-technical-documentation), utilizaremos a mesma abordagem
 * deste tema na criação de novos componentes (um documento genérico) com algumas exceções, como para workers ou semelhantes.
 * 
 * Futuramente a ideia é alterar os componentes já criados pelo tema também aqui ou em documento somente para tal, de maneira a facilitar a manutenção e auxiliar 
 * na atualização automática do tema de um jeito mais fácil
 */
import { 
    debounce,
    fetchCart
 } from "theme";

//Generic remove class for element (element, class);
 function removeElClass(el, c) {
    el.classList.remove(c);
 }

/**
 * Agência Mofo
 *  - Chamado #3945
 *  - Implementação de vistos recentemente personalizado,
 *  - com possibilidade de não repetir produtos,
 *  - com possibilidade de não repetir produtos (com o que está no carrinho)
 *  - Talvez pudesse ser implementado diretamente no JS original para tudo, mas assim há flexibilidade e facilita atualização do tema.
 *      * Se o comportamento de não repetir produtos no carrinho precisar ser para todos os RRecentlyVieweds, começa a fazer sentido aplicar diretamente em theme.js
 
 */
// js/sections/recently-viewed-products-block-extension.js
class RecentlyViewedProductsBlockExtension extends HTMLElement {
    constructor() {
        super();
        this.debounceTimer = 500; //Em ms
        this.parent = document
            .querySelector(`[data-extension-id=${this.dataset.extensionId}]`)
            .closest('recently-viewed-products');
    }

    connectedCallback() {        
        //Impedir chamada repetida do elemento
        // Segunda versão - trabalha com o item produto do cachê (id, price) e tem melhor otimização (set invés de array - reduzido de o(n^2))
        const debouncedStart = debounce(async () => {
            const items = (await fetchCart).items;

            if (!items.length) return;

            // Cria um Set com os ids dos produtos do carrinho
            const cartIds = new Set(items.map(item => item.product_id));

            let recentlyViewedProducts = JSON.parse(
                localStorage.getItem('theme:recently-viewed-products') || '[]'
            );

            if (!recentlyViewedProducts.length) return;

            
            let foundIndex = recentlyViewedProducts.findIndex(product => cartIds.has(product?.id));

            if (foundIndex === -1) return;

            // Remove o produto encontrado
            const [product] = recentlyViewedProducts.splice(foundIndex, 1);
            recentlyViewedProducts = recentlyViewedProducts.filter(prod => prod?.id !== product?.id);

            //Reseta cachê
            localStorage.setItem(
                'theme:recently-viewed-products',
                JSON.stringify(recentlyViewedProducts)
            );

            if (!this.dataset?.extensionId) return;

            const clone = this.parent.cloneNode(true);
            this.parent.replaceWith(clone);
            this.parent = clone;

        }, this.debounceTimer);

        debouncedStart();        
        removeElClass(this.parent, 'visibility-hidden');
    }
}

if (!window.customElements.get("recently-viewed-products-block-extension")) {
  window.customElements.define("recently-viewed-products-block-extension", RecentlyViewedProductsBlockExtension);
}

class ShippingQuote extends HTMLElement {
    constructor(){
        super();
        this.input = this.querySelector('.shipping-input');
        this.button = this.querySelector('button[type="submit"]');
        this.loadingSpinner = this.button?.querySelector('.loading__spinner');
        this.errorMessage = this.querySelector('.msg-error');
        this.address = this.querySelector('.address');
        this.shippingQuotes = this.querySelector('.shipping-quotes');
        this.baseUrl = window.Shopify?.routes?.root || '/';
    }

    connectedCallback() {
        //Input Listener para aplicar a máscara do cep
        this.input.addEventListener('input', ()=> {  
            // Remove tudo que não for dígito
            let maskedValue = this.input.value.replace(/\D/g, '');

            // Limita a 8 dígitos (sem o '-')
            if (maskedValue.length > 8) {
                maskedValue = maskedValue.substring(0, 8);
            }

            // Aplica a máscara: XXXXX-XXX (se tiver 5+ dígitos)
            if (maskedValue.length > 5) {
                maskedValue = maskedValue.replace(/^(\d{5})(\d{0,3})/, '$1-$2');
            }
            
            this.input.value = maskedValue;
        });
        
        //Acionado no clique - valida o cep digitado antes de iniciar
        this.button.addEventListener('click', (e)=> { 
            e.preventDefault();
            this.hideError();

            const value = this.input.value.trim();
            // Remove hífen para validação
            const cleanValue = value.replace(/-/g, '');
            
            // Confirma se tem 8 dígitos
            if (/^\d{8}$/.test(cleanValue)) {
                this.getShipping(cleanValue);
            } else {
                this.showError('CEP inválido! Deve ter 8 dígitos.');
            }
        });
    }

    //Controlam mensagens de erro
    hideError() { 
        this.input.classList.remove('error');
        this.errorMessage.classList.add('hidden');
    }
    showError(msg) {
        this.input.classList.add('error');
        
        this.errorMessage.innerHTML = `
            <span>
                <strong>${msg}</strong>
            </span>
            `;

        this.errorMessage.classList.remove('hidden');
        this.address.classList.add('hidden');
        this.shippingQuotes.classList.add('hidden');
        this.hideLoading();
    }

    //Controlam visual do Carregamento
    hideLoading() {
        if(!this.loadingSpinner) return;

        this.loadingSpinner.classList.add('hidden');
        this.button.querySelector('span').classList.remove('hidden');
    }
    showLoading() {
        if(!this.loadingSpinner) return;

        this.loadingSpinner.classList.remove('hidden');
        this.button.querySelector('span').classList.add('hidden');
    }

    //Atualiza visual com informações do endereço - somente se for possível o preenchimento
    updateAddress(address) {
        let strAddress = '';

        if (address?.logradouro) strAddress += address.logradouro;
        if (address?.bairro) strAddress += strAddress !== '' ? `, ${address.bairro}` : address.bairro;
        if (address?.localidade) strAddress += strAddress !== '' ? ` - ${address.localidade}` : address.localidade;
        if (address?.uf) strAddress += strAddress !== '' ? `, ${address.uf}` : address.uf;

        if (strAddress !== '') {
            this.address.innerHTML = `<span>${strAddress}</span>`;
            this.address.classList.remove('hidden');
        } else {
            this.address.classList.add('hidden');
        }
    }

    //Renderiza as opções de frete
    renderShippingQuotes(shippingQuotes) {
        if (!shippingQuotes || shippingQuotes?.lenght == 0) return;
        //Retira o resultado anterior (se houver) e esconde a div até que todos os itens sejam carregados
        this.shippingQuotes.textContent = '';
        this.shippingQuotes.classList.remove('hidden');

        //Para cada opção de frete - criar e inserir um <li> com as informações
        shippingQuotes.forEach(quote => {
            const deliveryTime = quote.delivery_days[0] != 1 
                ? 'dias úteis' 
                : 'dia útil';

            const price = quote.price > 0 
                ? `R$ ${quote.price.toString().replace('.', ',')}`
                : 'Grátis';

            //Modo de configurar nome para transportadora padrão
            let presentment_name = `<span class="shipping-carrier-title">
                    <strong>
                    ${quote.presentment_name.trim()}
                    </strong>
                </span>`;

            //Caso ela tenha retirada em loja
            //Talvez seja bom trocar o RETIRAR por algo que possamos alterar nas configurações do painel
            //Caso seja diferente em outra transportadora
            //Pior que acontece é o nome gigante, somente
            if(quote.presentment_name.includes('RETIRAR')) {
                const wordParts = quote.presentment_name.split('|');

                let temp = '';
                for(const [index,  part] of wordParts.entries()){
                    if(part.includes('RETIRAR')) {
                        temp += `<span class="shipping-carrier-title">
                                <strong>
                                ${part.trim()}
                                </strong>
                            </span>`;
                        continue;
                    }
                    if(index > 1) {
                        temp += `<span class="shipping-carrier-store-detail">${part.trim()}</span>`
                        continue;
                    }
                    temp += `<span class="shipping-carrier-store">${part.trim()}</span>`
                }
                presentment_name = temp;
            }

            const item = `
                <li class="shipping-quote">
                    <div class="shipping-carrier">
                        ${presentment_name}
                        <small>${quote.delivery_days[0]} ${deliveryTime}</small>
                    </div>
                    <div class="shipping-price">
                        <span>
                            <strong>
                                ${price}
                            </strong>
                        </span>
                    </div>
                </li>
                `;
            this.shippingQuotes.insertAdjacentHTML('beforeend', item);          
        });
    }


    //Lógica principal
    async getShipping(cep) {
        try {
            //Controle visual (inicia loadingSpinner e esconde resultados anteriores)
            this.showLoading();
            this.shippingQuotes.classList.add('hidden');
            this.address.classList.add('hidden');

            //Pega o endereço para verificar se o mesmo existe
            //E para que o cliente possa confirmar que está correto
            const address = await this.getCEP(cep);
            if(!address || address?.erro) {
                this.showError('CEP informado é inválido.');
                return;
            }
            //Renderiza na tela
            this.updateAddress(address);

            //Pegar itens atuais do carrinho e já usá-los como backup (se houver)
            const cartItems = await this.getCart();
            //Adiciona pelo menos uma quantidade de itens ao carrinho, para frete atualizado (pega dados do form de produto)
            await this.addTempItems();
            
            //Valores
            const shippingQuotes = await this.getShippingQuote(cep, address.uf);

            //Limpar carrinho (reset)
            await this.clearCart();
            
            //Readicionar produtos do carrinho anterior, se houver
            if(cartItems?.length) {
                const taggedItems = cartItems.map(item => ({
                    ...item,
                    properties: { ...(item.properties || {}), _shipping_calc: true }
                }));
                await this.updateCart(taggedItems);
            }

            //Renderizar opções de frete e finalizar loadingSpinner
            this.renderShippingQuotes(shippingQuotes);
            this.hideLoading();
        } catch(e) {
            console.error('Error: ', e);
            this.showError('Erro: verifique o CEP e tente novamente.');
        }
    }

    //Chamada API para limpar (todo) o carrinho
    //Poderia estar num Service em separado para melhor manutenção (CartService)
    clearCart() {
        return fetch(`${this.baseUrl}cart/clear.js`)
                .then(response => {
                    if(!response.ok) {
                        console.error('Erro na requisição');
                        throw new Error;
                    }
                })
                .catch(e => console.error('Error:', e))
    }

    //Caso estejamos na página de produtos
    //Adicionar ao carrinho o item atual para cálculo correto do frete
    //Poderia estar num Service em separado para melhor manutenção (CartService)
    //Como o tema não utiliza o elemento <product-info>, mas sim <safe-sticky> 
    //com a classe .product-info, optamos por buscar a classe.
    async addTempItems() {
        const formElement = this.closest('.product-info')?.querySelector('form[action*="/cart/add"]');
        if(!formElement) return null;

        const formData = this.getFormData(formElement);

        //Marca a requisição como originada da calculadora de frete, para que o
        //interceptor global de fetch (bloqueio de variante não selecionada) não
        //a trate como uma tentativa real de "Adicionar à Sacola" (chamado da imagem
        //em que o CEP era calculado e o erro de variante aparecia indevidamente).
        formData.properties = { ...(formData.properties || {}), _shipping_calc: true };

        const results = await this.updateCart(formData);
    }

    //Atualizar o carrinho, adicionando elemento ou elementos
    //Poderia estar num Service em separado para melhor manutenção (CartService)
    updateCart(items) {
        //Se um vetor de items
        if(items?.length) {
            return fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                'Content-Type': 'application/json'
                },
                body: JSON.stringify({ items })
            })
            .then(response => response.json())
            .catch(e => {
                console.error('Erro:', e);
                return null;
            });
        }
        //Se somente um item
        return fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                'Content-Type': 'application/json'
                },
                body: JSON.stringify(items)
            })
            .then(response => response.json())
            .catch(e => {
                console.error('Erro:', e);
                return null;
            });
    }
    
    //Pega as opções de frete utilizando a própria Shopify através da AJAX API (https://shopify.dev/docs/api/ajax/reference/cart#generate-shipping-rates);
    //Exemplo de URL: cart/shipping_rates.json?shipping_address[zip]=CEP&shipping_address[country]=PAÍS&shipping_address[province]=ESTADO ou DISTRITO
    //Poderia estar num Service em separado para melhor manutenção (shippingService)
    getShippingQuote(cep, province) {
        const paramsUrl = `cart/shipping_rates.json?shipping_address[zip]=${cep}&shipping_address[country]=BR&shipping_address[province]=${province}`;
        return fetch(`${this.baseUrl}${paramsUrl}`)
                .then(response => {
                    if(response.ok) {
                        return response.json()
                    }
                })
                .then(data =>{
                    if (data.shipping_rates.length > 0) {
                        return data.shipping_rates;
                    }
                    return null;
                })
                .catch(e => console.error('Error: ', e));
    }

    //Faz requisição a VIACEP para validação e utilizar endereço do cliente (estado/distrito)
    //Poderia ser utilizada a BRASILAPI, mas os atributos seriam outros, ex: resultado.estado seria resultado.province
    //Para realizar a troca deve ser reescrita essa parte no getShippingQuotes ou fazer com que seja genérica o bastante para ambos.
    //Poderia estar num Service em separado para melhor manutenção (AddressService?)
    getCEP(value) {
        return fetch(`https://viacep.com.br/ws/${value}/json/`)
            .then(response => response.json())
            .then(data => {
                if (!data.erro) {
                    return data;
                }
            })
            .catch(e => console.error('Error na validação ', e));
    }

    //Pega os itens atuais do Cart (que serão re-adicionados posteriormente)
    //Poderia estar num Service em separado para melhor manutenção (CartService)
    getCart() {
        return fetch(`${this.baseUrl}cart.js`)
            .then(response => {
                if (!response.ok) throw new Error('Falha ao carregar carrinho');
                return response.json();
            })
            .then(dataTransfer => {
                if (!dataTransfer.items || dataTransfer.items.length === 0) {
                    return null
                }
                return dataTransfer.items;
            })
            .catch(e => console.error('Erro ao carregar o carrinho: ', e));
    }

    //Função Genérica para transformar valores do formulário em um Objeto.
    //Pode (e talvez deva) ser implementada no Utils
    getFormData(form, options = {}) {
        const { includeEmpty = false, transform } = options;
        const formData = new FormData(form);
        const result = {};

        formData.forEach((value, key) => {
            // Handle empty values
            if (!includeEmpty && (value === '' || value === null || value === undefined)) {
                return;
            }

            // Apply transform function if provided
            const processedValue = transform ? transform(key, value) : value;

            // Handle multiple values for same key (e.g., checkboxes)
            if (key in result) {
                if (Array.isArray(result[key])) {
                    result[key].push(processedValue);
                } else {
                    result[key] = [result[key], processedValue];
                }
            } else {
                result[key] = processedValue;
            }
        });

        return result;
    }
}

//Inicialização padrão de elemento HTML personalizado
if (!customElements.get('shipping-quote')) {
    try {
        customElements.define('shipping-quote', ShippingQuote);
    } catch (err) {
        console.error('Element already defined:', err);
    }
}

class MobileAnchor extends HTMLElement {
    connectedCallback() {
        const button = this.querySelector('button.mobile-anchor-button');
        if (!button) return;

        const clickHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            const targetId = button.getAttribute('data-target');
            let targetElement = null;

            if (targetId && targetId.startsWith('#')) {
                targetElement = document.querySelector(targetId);
            }

            // Fallback para o elemento comum do tema
            if (!targetElement) {
                targetElement = document.getElementById('product-extra-information');
            }

            if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        };

        button.addEventListener('click', clickHandler);
        // Guarda para remover se necessário (opcional)
        button._clickHandler = clickHandler;
    }
}

if (!customElements.get('mobile-anchor')) {
    customElements.define('mobile-anchor', MobileAnchor);
}
/* Tradução completa da data (Meses e Prefixo) - Anna Prata */
const translateWishlistDate = () => {
    const dateElements = document.querySelectorAll('.wishlist-hero-product-expiry');
    
    const meses = {
        'Jan': 'Jan',
        'Feb': 'Fev',
        'Mar': 'Mar',
        'Apr': 'Abr',
        'May': 'Mai',
        'Jun': 'Jun',
        'Jul': 'Jul',
        'Aug': 'Ago',
        'Sep': 'Set',
        'Oct': 'Out',
        'Nov': 'Nov',
        'Dec': 'Dez'
    };

    dateElements.forEach(el => {
        let text = el.innerText;
        
        // Traduz os meses baseando-se no objeto acima
        Object.keys(meses).forEach(mesIngles => {
            const regex = new RegExp(mesIngles, 'g');
            text = text.replace(regex, meses[mesIngles]);
        });

        // Garante que o texto final seja aplicado
        if (el.innerText !== text) {
            el.innerText = text;
        }
    });
};


// Adicione este script ao seu tema - Chamados #4017 e #4063
document.addEventListener('DOMContentLoaded', function() {
  const selosBadges = document.querySelectorAll('.badge--selos');
  
  selosBadges.forEach(badge => {
    // Aplicar variáveis CSS
    if (badge.dataset.backgroundColor) {
      badge.style.setProperty('--selo-background-color', badge.dataset.backgroundColor);
    }
    if (badge.dataset.textColor) {
      badge.style.setProperty('--selo-text-color', badge.dataset.textColor);
    }
    if (badge.dataset.fontSize) {
      badge.style.setProperty('--selo-font-size', badge.dataset.fontSize + 'px');
    }
    if (badge.dataset.gradientStart && badge.dataset.gradientEnd) {
      badge.style.setProperty('--selo-gradient-start', badge.dataset.gradientStart);
      badge.style.setProperty('--selo-gradient-end', badge.dataset.gradientEnd);
    }
    if (badge.dataset.gradientDirection) {
      badge.style.setProperty('--selo-gradient-direction', badge.dataset.gradientDirection);
    }
  });
});

(function () {
  function injectKonfidencyStyle() {
    if (document.getElementById('ap-konfidency-hide-style')) return;

    var style = document.createElement('style');
    style.id = 'ap-konfidency-hide-style';
    style.textContent = `
      .konfidency-summary > span:not(.review-count-only) {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }
}());

// Agência Mofo - Bloqueio de compra sem variante selecionada
(function () {
  const originalFetch = window.fetch;
  const SELECT_MSG = 'Selecione a opção que deseja.';

  // ─── Estilos ───────────────────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById('mofo-variant-styles')) return;
    const style = document.createElement('style');
    style.id = 'mofo-variant-styles';
    style.textContent = `
      .mofo-variant-picker-highlight {
        animation: mofo-pulse 0.8s ease 2;
        outline: 2px solid #d32f2f;
        outline-offset: 6px;
        border-radius: 4px;
      }
      @keyframes mofo-pulse {
        0%   { box-shadow: 0 0 0 0 rgba(211,47,47,0.4); }
        70%  { box-shadow: 0 0 0 10px rgba(211,47,47,0); }
        100% { box-shadow: 0 0 0 0 rgba(211,47,47,0); }
      }
      .mofo-variant-error-msg {
        color: #d32f2f;
        font-size: 0.85rem;
        margin-top: 8px;
        display: none;
      }
    `;
    document.head.appendChild(style);
  }

  // ─── Lê os valores dos radios marcados por posição ────────────────────
  function getCheckedOptionValues(picker) {
    const selectors = picker.querySelectorAll('[data-option-selector]');
    const values = [];
    for (const selector of selectors) {
      const checked = selector.querySelector('input[type="radio"]:checked');
      values.push(checked ? checked.value : null);
    }
    return values; // ex: ['Dourado', 'P'] ou [null, null]
  }

  // ─── Verifica se todas as opções foram selecionadas ───────────────────
  function hasVariantSelected(picker) {
    return getCheckedOptionValues(picker).every(v => v !== null);
  }

  // ─── Cache local de produtos (substitui ProductLoader) ────────────────
  const _productCache = {};

  async function loadProduct(handle) {
    if (_productCache[handle]) return _productCache[handle];
    _productCache[handle] = fetch(`${Shopify.routes.root}products/${handle}.js`)
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
    return _productCache[handle];
  }

  // ─── Resolve a variante correta pelo produto e opções marcadas ────────
  async function resolveVariantId(picker) {
    const handle = picker.getAttribute('handle');
    if (!handle) return null;

    const optionValues = getCheckedOptionValues(picker);
    if (optionValues.some(v => v === null)) return null;

    const product = await loadProduct(handle);
    if (!product) return null;

    const variant = product.variants.find(v =>
      v.options.every((val, idx) => val === optionValues[idx])
    );
    return variant ? variant.id : null;
  }

  // ─── Mostra erro ───────────────────────────────────────────────────────
  function showError(picker) {
  // Remove e readiciona a classe para reiniciar a animação
  picker.classList.remove('mofo-variant-picker-highlight');
  void picker.offsetWidth; // força reflow
  picker.classList.add('mofo-variant-picker-highlight');
  setTimeout(() => picker.classList.remove('mofo-variant-picker-highlight'), 1600);

  // Verifica se o picker está dentro de um modal de quick buy
  const quickBuyModal = picker.closest('quick-buy-modal');
  let containerToScroll = window; // padrão: página inteira

  if (quickBuyModal) {
    // Tenta obter o elemento de conteúdo rolável do modal (shadow DOM)
    // O modal usa 'part="content"' no shadow root
    const shadowContent = quickBuyModal.shadowRoot?.querySelector('[part="content"]');
    if (shadowContent) {
      containerToScroll = shadowContent;
    } else {
      // Fallback: o próprio modal
      containerToScroll = quickBuyModal;
    }
  }

  // Função para rolar suavemente até o picker
  const scrollToPicker = () => {
    if (containerToScroll === window) {
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      // Calcula a posição relativa ao container rolável
      const pickerRect = picker.getBoundingClientRect();
      const containerRect = containerToScroll.getBoundingClientRect();
      // Desloca o container para que o picker fique centralizado
      const offsetY = pickerRect.top - containerRect.top + containerToScroll.scrollTop 
                      - containerRect.height / 2 + pickerRect.height / 2;
      containerToScroll.scrollTo({ top: offsetY, behavior: 'smooth' });
    }
  };
const allErrors = picker.parentNode.querySelectorAll('.mofo-variant-error-msg');

allErrors.forEach((error, index) => {
  if (index > 0) error.remove();
});
  // Insere a mensagem de erro
  let msg = picker.parentNode.querySelector('.mofo-variant-error-msg');
  if (!msg) {
    msg = document.createElement('p');
    msg.className = 'mofo-variant-error-msg';
    msg.setAttribute('role', 'alert');
    msg.textContent = SELECT_MSG; // definido no início do script
    picker.parentNode.insertBefore(msg, picker.nextSibling);
  }
  msg.style.display = 'block';

  // Rola após inserir a mensagem (pequeno delay para garantir que a mensagem apareça)
  setTimeout(scrollToPicker, 100);
}

  function hideError(picker) {
    const msg = picker.parentNode.querySelector('.mofo-variant-error-msg');
    if (msg) msg.style.display = 'none';
  }

  // ─── Limpa radios visualmente ─────────────────────────────────────────
  function clearRadios(picker) {
    picker.querySelectorAll('input[data-option-position]').forEach(i => { i.checked = false; });
    picker.querySelectorAll('variant-option-value').forEach(el => { el.textContent = ''; });
  }

  // ─── Setup listeners por picker ───────────────────────────────────────
  function setupPicker(picker) {
    if (picker._mofoInit) return;
    picker._mofoInit = true;

    setTimeout(() => clearRadios(picker), 50);

    picker.addEventListener('change', function (e) {
      if (e.target.type === 'radio') {
        if (hasVariantSelected(picker)) hideError(picker);
      }
    });
  }

  // ─── Rastreia o <form> que disparou o envio ────────────────────────────
  // Precisamos capturar isso no evento "submit" (fase de captura), pois o
  // ProductForm do tema desabilita o botão de submit logo em seguida
  // (theme.js: submitButton.setAttribute('disabled', 'disabled')), o que tira
  // o foco do elemento. Se tentássemos usar document.activeElement dentro do
  // fetch interceptado (mais abaixo), o botão já não estaria mais focado.
  let lastSubmittedForm = null;
  document.addEventListener('submit', function (e) {
    lastSubmittedForm = e.target;
  }, true);

  // ─── Intercepta fetch cart/add.js ─────────────────────────────────────
window.fetch = async function (url, options, ...rest) {
  const urlStr = typeof url === 'string' ? url : url?.url || '';

  if (urlStr.includes('cart/add.js') && options?.method === 'POST') {
    // Ignora requisições do ap-gift-campaign (identificadas pela propriedade _campanha)
    // e requisições da calculadora de frete (identificadas pela propriedade _shipping_calc),
    // que adiciona/remove itens temporariamente e não deve disparar a validação de variante.
    try {
      const body = typeof options?.body === 'string' ? JSON.parse(options.body) : null;
      const isCampanha = body?.properties?._campanha;
      const isShippingCalc = body?.properties?._shipping_calc
        || (Array.isArray(body?.items) && body.items.some((item) => item?.properties?._shipping_calc));

      if (isCampanha || isShippingCalc) {
        return originalFetch(url, options, ...rest);
      }
    } catch (e) { /* ignora se não for JSON */ }

    // Identifica o form que efetivamente disparou este envio (capturado no
    // listener de "submit" acima) e "consome" a referência, para não reaproveitar
    // um valor antigo em uma eventual chamada futura não originada de um submit real.
    const submittedForm = lastSubmittedForm;
    lastSubmittedForm = null;

    // O variant-picker não fica necessariamente dentro do <form> na árvore do
    // DOM — ele se associa a ele através do atributo "form" (igual um input
    // form-associated do HTML5), então localizamos o picker pelo atributo
    // form="<id do form submetido>" em vez de por proximidade/DOM ou pelo
    // elemento focado (que pode já ter perdido o foco quando o botão é desabilitado).
    let picker = null;
    if (submittedForm && submittedForm.id) {
      picker = document.querySelector(
        `variant-picker[no-default-selection][form="${CSS.escape(submittedForm.id)}"]`
      );
    }

    // Fallback: se por algum motivo não foi possível identificar o form submetido
    // (ex.: chamada de cart/add.js disparada fora de um submit de formulário),
    // mantém o comportamento restrito ao quick-buy-modal aberto, se houver — nunca
    // faz mais busca "solta" na página inteira, que era a causa do produto errado
    // ser adicionado.
    if (!picker) {
      const quickBuyModal = document.querySelector('quick-buy-modal[open]');
      if (quickBuyModal) {
        picker = quickBuyModal.querySelector('variant-picker[no-default-selection]');
      }
    }

    if (picker) {
      // Bloqueia se não há seleção
      if (!hasVariantSelected(picker)) {
        showError(picker);
        return Promise.resolve(new Response(
          JSON.stringify({ description: SELECT_MSG }),
          { status: 422, headers: { 'Content-Type': 'application/json' } }
        ));
      }

      // Resolve o ID correto a partir dos radios — ignora o masterSelector
      const correctId = await resolveVariantId(picker);
      if (correctId && options.body instanceof FormData) {
        options.body.set('id', correctId);
      } else if (correctId && typeof options.body === 'string') {
        try {
          const parsed = JSON.parse(options.body);
          parsed.id = correctId;
          options.body = JSON.stringify(parsed);
        } catch (e) { /* ignora se não for JSON */ }
      }
    }
  }

  return originalFetch(url, options, ...rest);
};

  // ─── Init ──────────────────────────────────────────────────────────────
  function init() {
    injectStyles();
    document.querySelectorAll('variant-picker[no-default-selection]').forEach(setupPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ─── Wishlist Hero: re-init após AJAX de produtos relacionados e recentemente vistos ───
  // Ambos os Web Components (product-recommendations e recently-viewed-products) usam
  // replaceChildren() para injetar cards via AJAX, depois que o Wishlist Hero já inicializou.
  // Interceptamos o replaceChildren de cada elemento para disparar o re-init do app.
  (function () {
    function reinitWishlistHero() {
      // Tenta os métodos públicos conhecidos do Wishlist Hero
      if (window.WishlistHero && typeof window.WishlistHero.init === 'function') {
        window.WishlistHero.init();
        return;
      }
      if (window.wishlisthero && typeof window.wishlisthero.init === 'function') {
        window.wishlisthero.init();
        return;
      }
      // Fallback: dispara eventos customizados que o app pode escutar
      document.dispatchEvent(new CustomEvent('wishlisthero:reinit'));
      document.dispatchEvent(new CustomEvent('wlh:reinit'));

      // Fallback final: recarrega o script do app para forçar nova varredura do DOM
      var scripts = document.querySelectorAll('script[src*="wishlisthero"], script[src*="wishlist-hero"]');
      scripts.forEach(function (s) {
        var newScript = document.createElement('script');
        newScript.src = s.src + (s.src.includes('?') ? '&' : '?') + '_t=' + Date.now();
        document.body.appendChild(newScript);
      });
    }

    function patchElement(el) {
      if (!el || el.__wlhPatched) return;
      el.__wlhPatched = true;

      var original = el.replaceChildren.bind(el);
      el.replaceChildren = function () {
        original.apply(el, arguments);
        setTimeout(reinitWishlistHero, 300);
      };
    }

    var selectors = ['product-recommendations', 'recently-viewed-products'];

    var globalObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          selectors.forEach(function (selector) {
            if (node.matches && node.matches(selector)) {
              // Aguarda o connectedCallback do Web Component antes de patchar
              setTimeout(function () { patchElement(node); }, 0);
            }
            if (node.querySelectorAll) {
              node.querySelectorAll(selector).forEach(function (el) {
                setTimeout(function () { patchElement(el); }, 0);
              });
            }
          });
        });
      });
    });

    globalObserver.observe(document.body, { childList: true, subtree: true });

    // Patcha elementos já presentes no DOM ao carregar
    selectors.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (el) {
        setTimeout(function () { patchElement(el); }, 0);
      });
    });
  })();
})();